import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { RxHamburgerMenu } from "react-icons/rx";
import { IoClose, IoChevronDown } from "react-icons/io5";
import { AiOutlineGlobal } from "react-icons/ai";
import IPPS from "../../assets/logo/IPPS_Loco_CC_Original-01.png";
import { WhyUs, Products, Solutions, Resources } from "./Links";
import { useLanguage } from "../../i18n/LanguageContext";

function useEsc(handler) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && handler?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handler]);
}

function useOnClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler?.();
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

function DesktopFlyout({ label, FlyoutContent }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEsc(() => setOpen(false));
  useOnClickOutside(ref, () => setOpen(false));

  return (
    <div
      ref={ref}
      className="relative group"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        className="flex items-center gap-1.5 px-3 lg:h-20 uppercase text-[13px] font-semibold tracking-wide text-darkBlue/70 hover:text-darkBlue transition-colors duration-200"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <IoChevronDown
          className={`text-xs transition-transform duration-300 ${open ? "rotate-180" : "rotate-0"}`}
        />
      </button>

      <AnimatePresence>
        {open && FlyoutContent && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.97 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            role="menu"
            className="absolute left-0 top-full z-50 mt-1"
            onClick={() => setOpen(false)}
          >
            <FlyoutContent />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileSection({ section, onLinkClick }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100">
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-left uppercase font-semibold text-darkBlue/80 hover:text-darkBlue transition-colors duration-200"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>{section.label}</span>
        <IoChevronDown
          className={`transition-transform ${open ? "rotate-180" : "rotate-0"}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <ul className="px-2 pb-2">
              {section.items?.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={onLinkClick}
                    className={({ isActive }) =>
                      `block px-3 py-2 rounded-lg text-sm ${
                        isActive
                          ? "bg-blue/10 text-blue font-medium"
                          : "text-darkBlue/60 hover:bg-blue/5 hover:text-darkBlue"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function LanguageToggle({ dark = false }) {
  const { lang, toggleLanguage, t } = useLanguage();

  return (
    <button
      onClick={toggleLanguage}
      className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs uppercase font-semibold transition-colors duration-200 ${
        dark
          ? "border-white/20 bg-white/10 text-white/80 hover:text-white hover:bg-white/20"
          : "border-blue/15 bg-blue/5 text-darkBlue/70 hover:text-darkBlue hover:bg-blue/10"
      }`}
      aria-label={t("nav.toggleLanguage")}
    >
      <AiOutlineGlobal className="text-sm" />
      <span>{lang.toUpperCase()}</span>
    </button>
  );
}

export default function NavBar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useLanguage();

  const nav = [
    {
      key: "why-us",
      label: t("nav.sections.whyUs"),
      flyout: WhyUs,
      items: [
        { to: "/ourCompany", label: t("nav.items.ourCompany") },
        { to: "/ourMission", label: t("nav.items.ourMission") },
      ],
    },
    {
      key: "products",
      label: t("nav.sections.products"),
      flyout: Products,
      items: [
        { to: "/products/treasury", label: t("nav.items.treasury") },
        {
          to: "/products/virtual-account",
          label: t("nav.items.virtualAccounts"),
        },
        { to: "/products/e-wallets", label: t("nav.items.eWallets") },
        {
          to: "/products/invoice-QR-Integration",
          label: t("nav.items.invoiceQr"),
        },
      ],
    },
    {
      key: "solutions",
      label: t("nav.sections.solutions"),
      flyout: Solutions,
      items: [
        { to: "/solutions#b2b-payments", label: t("nav.items.b2bPayments") },
        { to: "/solutions#order-to-cash", label: t("nav.items.orderToCash") },
        {
          to: "/solutions#record-to-report",
          label: t("nav.items.recordToReport"),
        },
        {
          to: "/solutions#cash-management",
          label: t("nav.items.cashManagement"),
        },
        {
          to: "/solutions#account-payables",
          label: t("nav.items.accountPayables"),
        },
      ],
    },
    {
      key: "resources",
      label: t("nav.sections.resources"),
      flyout: Resources,
      items: [{ to: "/usecase", label: t("nav.items.useCases") }],
    },
    {
      key: "contact",
      label: t("nav.sections.contact"),
      to: "/contact",
    },
  ];

  useEsc(() => setMobileOpen(false));

  return (
    <>
      <nav className="fixed top-0 z-40 w-full bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="mx-auto lg:w-full max-w-screen-2xl px-5 pr-4">
          <div className="flex h-16 lg:h-20 items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={IPPS} alt="IPPS" className="h-10 w-auto" />
            </Link>

            <div className="hidden lg:flex items-center gap-2">
              {nav.map((s) =>
                s.to ? (
                  <Link
                    key={s.key}
                    to={s.to}
                    className="flex items-center px-3 lg:h-20 uppercase text-[13px] font-semibold tracking-wide text-darkBlue/70 hover:text-darkBlue transition-colors duration-200"
                  >
                    {s.label}
                  </Link>
                ) : (
                  <DesktopFlyout
                    key={s.key}
                    label={s.label}
                    FlyoutContent={s.flyout}
                  />
                ),
              )}
            </div>

            <div className="flex items-center gap-2">
              <div className="hidden lg:block">
                <LanguageToggle />
              </div>
              <button
                className="lg:hidden inline-flex items-center justify-center rounded-lg border border-blue/15 bg-blue/5 p-2 text-darkBlue/70 hover:text-darkBlue hover:bg-blue/10 transition-colors duration-200"
                onClick={() => setMobileOpen(true)}
                aria-label={t("nav.openMenu")}
              >
                <RxHamburgerMenu size={20} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="mobile-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[9998] bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mobileOpen && (
          <motion.aside
            key="mobile-panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.22 }}
            className="fixed right-0 top-0 z-[9999] h-full w-full overflow-y-auto bg-white text-darkBlue shadow-2xl"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-between px-4 py-4">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2"
              >
                <img src={IPPS} alt="IPPS" className="h-7 w-auto" />
              </Link>
              <button
                className="inline-flex items-center justify-center rounded-xl border border-blue/15 bg-blue/5 p-2 text-darkBlue/70 hover:text-darkBlue hover:bg-blue/10 transition-colors duration-200"
                onClick={() => setMobileOpen(false)}
                aria-label={t("nav.closeMenu")}
              >
                <IoClose size={22} />
              </button>
            </div>

            <div className="pb-16">
              {nav.map((section) =>
                section.to ? (
                  <div key={section.key} className="border-b border-gray-100">
                    <NavLink
                      to={section.to}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `block px-4 py-3 uppercase font-semibold ${
                          isActive
                            ? "text-orange"
                            : "text-darkBlue/80 hover:text-orange"
                        } transition-colors duration-200`
                      }
                    >
                      {section.label}
                    </NavLink>
                  </div>
                ) : (
                  <MobileSection
                    key={section.key}
                    section={section}
                    onLinkClick={() => setMobileOpen(false)}
                  />
                ),
              )}

              <div className="px-4 py-4">
                <LanguageToggle />
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
